import { useEffect, useState } from "react";
import { breadcrumbState } from "../../../lib/stores/breadcrumb";
import { useRecoilState } from "recoil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post, put } from "../../../services/apiService";
import getApiEndpoints from "../../../services/endpoints";
import {
  Form,
  Button,
  message,
  Select,
  notification,
  Row,
  Col,
  Input,
  Switch,
} from "antd";
import FormBuilder from "antd-form-builder";
import { emailValidator } from "../../../lib/utils/form-validator";
import { showApiError, isApiSuccess } from "../../../lib/utils/handleApiError";
import "./page.css";

const LocationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [companyDropdown, setCompanyDropdown] = useState([]);
  const [, setBreadCrumb] = useRecoilState(breadcrumbState);
  const [form] = Form.useForm();
  const user: any =
    localStorage.getItem("user") || sessionStorage.getItem("user");
  const userDataNew: any = JSON.parse(user);
  const userRole: any = userDataNew?.roles[0];
  const apiEndpoints: any = getApiEndpoints(userRole);
  const [statesDropdown, setStatesDropdown] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [outletData, setOutletData] = useState([]);
  const [menuData, setMenuData] = useState([]);

  const isEdit = location.pathname.includes("editLocation");

  useEffect(() => {
    setBreadCrumb([
      { label: "Masters", href: "/locations" },
      { label: "Location", href: "/locations" },
      {
        label: id ? "Edit Location" : "Add Location",
        href: id ? `/locations/${id}` : "/locations/add",
      },
    ]);
  }, [setBreadCrumb]);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const response = await get(apiEndpoints.getAllCompanies);
        if (isApiSuccess(response) && response.data) {
          setCompanyDropdown(response.data);
        } else {
          showApiError(response, "Failed to load companies");
        }
      } catch (error) {
        showApiError(error, "Failed to load companies");
      }
    };
    fetchOrg();
  }, []);

  const fetchById = async () => {
    try {
      const response = await get(
        apiEndpoints.getLocationByLocationId + `/${id}`
      );
      if (isApiSuccess(response)) {
        const initialValues = {
          ...response?.data[0],
          active: response?.data[0]?.isActive === 1 ? true : false,
          outletId: response?.data[0]?.outletId
            ? response?.data[0]?.outletId
            : null,
        };
        form.setFieldsValue(initialValues);
      } else {
        showApiError(response, "Failed to load location");
      }
    } catch (error) {
      showApiError(error, "Failed to load location");
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchById();
    } else {
      form.setFieldsValue({ active: true });
    }
  }, [location]);

  const fetchOutlets = async () => {
    try {
      const response = await get(apiEndpoints.outletgroup);
      if (isApiSuccess(response)) {
        setOutletData(response?.data);
      } else {
        showApiError(response, "Failed to load outlet groups");
      }
    } catch (error) {
      showApiError(error, "Failed to load outlet groups");
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await get(apiEndpoints.menus);
      if (isApiSuccess(response)) {
        setMenuData([
          {
            menuId: null,
            menuName: "Select 1",
            updatedAt: "2024-10-05T12:34:35.225+00:00",
          },
          ...response?.data,
        ]);
      } else {
        showApiError(response, "Failed to load menus");
      }
    } catch (error) {
      showApiError(error, "Failed to load menus");
    }
  };

  const handleData = async () => {
    try {
      const dropdownResponse = await get(apiEndpoints.getAllStates);
      if (isApiSuccess(dropdownResponse)) {
        setStatesDropdown(dropdownResponse?.data);
      } else {
        showApiError(dropdownResponse, "Failed to load states");
      }
    } catch (error) {
      showApiError(error, "Failed to load states");
    }
  };

  useEffect(() => {
    handleData();
    fetchOutlets();
    fetchMenus();
  }, []);

  const onFinish = async (values: any) => {
    try {
      const modifiedValues = {
        ...values,
        locId: id,
        locationId: values?.locationId?.trim(),
        active: values?.active ? 1 : 0, // remove this line if backend accepts boolean
        outletId: values?.outletId || null,
        menuId: values?.menuId || null,
      };

      setIsLoading(true);
      const endPoint = id ? "updateLocationMaster" : "addLocationMaster";
      const method = id ? put : post;
      const response: any = await method(
        apiEndpoints[endPoint],
        modifiedValues
      );

      console.log("save response:", response); // remove after debugging

      if (isApiSuccess(response)) {
        notification.success({
          message: id ? "Updated Successfully" : "Created Successfully",
        });
        navigate("/locations");
      } else {
        // wrapper returned the error, HTTP 200 with status -1, or undefined
        showApiError(response, id ? "Update Failed" : "Create Failed");
      }
    } catch (error) {
      showApiError(error, id ? "Update Failed" : "Create Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error("Please fill in all fields");
  };

  const companyFormMeta: any = [
    {
      name: "companyId",
      label: "Company Name",
      type: "select",
      placeholder: "Select Company Name",
      rules: [{ required: true, message: "Select a Company Name!" }],
      widget: Select,
      widgetProps: {
        options: companyDropdown?.map((c: any) => ({
          value: c.companyId,
          label: c.companyName,
        })),
      },
    },
    {
      name: "locationName",
      label: "Name",
      type: "input",
      placeholder: "Enter Location Name",
      rules: [
        { required: true, message: "This field is required" },
        { min: 4, message: "Name must be at least 4 characters" },
      ],
    },
    {
      name: "locationAddress",
      label: "Address",
      type: "input",
      placeholder: "Enter Company Address",
      rules: [{ required: true, message: "This field is required" }],
    },
    {
      name: "stateCode",
      label: "State Name",
      type: "select",
      placeholder: "Select State Name",
      rules: [{ required: true, message: "Select a State Name!" }],
      widget: Select,
      widgetProps: {
        options: statesDropdown?.map((c: any) => ({
          value: c.stateCode,
          label: c.stateName,
        })),
      },
    },
    {
      name: "contactName",
      label: "Contact Name",
      type: "input",
      placeholder: "Enter Contact Name",
    },
    {
      name: "contactNumber",
      label: "Contact Number",
      type: "number",
      placeholder: "Enter Company Number",
      widgetProps: { maxLength: 10 },
      rules: [],
    },
    {
      name: "contactEmail",
      label: "Contact Email",
      type: "input",
      placeholder: "Enter Company Email",
      rules: [
        {
          validator: async (value: any) => {
            return emailValidator(value, false);
          },
        },
      ],
    },
    {
      name: "latitude",
      label: <div className="label-as">Latitude</div>,
      type: "input",
      placeholder: "Enter Latitude",
      rules: [],
    },
    {
      name: "longitude",
      label: <div className="label-as">Longitude</div>,
      type: "input",
      placeholder: "Enter Longitude",
      rules: [],
    },
    {
      name: "menuId",
      label: "Menu",
      type: "select",
      placeholder: "Select Menu",
      widget: Select,
      widgetProps: {
        options: menuData?.map((c: any) => ({
          value: c.menuId,
          label: c.menuName,
        })),
      },
    },
    {
      name: "outletId",
      label: "Outlet Group",
      type: "select",
      placeholder: "Select outlet Group",
      widget: Select,
      widgetProps: {
        options: [
          { value: null, label: "Select 1" },
          ...outletData?.map((c: any) => ({
            value: c.outletId,
            label: c.outletName,
          })),
        ],
      },
    },
  ];

  return (
    <>
      <div
        className="filters"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
          initialValues={{}}
          className="company-form"
        >
          <Row gutter={16}>
            {/* Location Id: editable on Add, read-only on Edit. Backend requires exactly 6 characters */}
            <Col span={8}>
              <Form.Item
                name="locationId"
                label="Location Id"
                rules={[
                  { required: true, message: "Please enter Location Id" },
                  {
                    len: 6,
                    message: "Location Id must be exactly 6 characters",
                  },
                  {
                    pattern: /^[A-Za-z0-9]+$/,
                    message: "Only letters and numbers allowed",
                  },
                ]}
              >
                <Input
                  placeholder="Enter 6 character Location Id"
                  maxLength={6}
                  disabled={isEdit}
                  style={{ width: "67%" }}
                />
              </Form.Item>
            </Col>
            {companyFormMeta?.map((item: any, index: any) => (
              <Col span={8} key={index}>
                <FormBuilder meta={[item]} form={form} />
              </Col>
            ))}
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="InActive" />
            </Form.Item>
          </Row>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              style={{ marginRight: "10px" }}
            >
              {id ? "Update Location" : "Add Location"}
            </Button>
            <Button onClick={() => navigate("/locations")}>Cancel</Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default LocationForm;